import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CalendarToday, School, Storage, VideoLibrary, InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { getScormStats } from '../../apis/scormStats';
import { getActiveSubscription } from '../../apis/subscription';
import { loginSuccess } from '../../store/slices/authSlice';
import files from '../../assets/files.svg';
import space from '../../assets/space.svg';
import completed from '../../assets/completed.svg';
import failed from '../../assets/failed.svg';
import SubscriptionAlert from '../subscription/Status/SubscriptionAlerts';
import FileStatusListener from './FileStatusListener';

const Stats = () => {
    const dispatch = useDispatch();
    const [stats, setStats] = useState({
        total_files: 0,
        total_size_mb: 0,
        total_extractions: 0,
        file_status_breakdown: [],
        extraction_status_breakdown: [],
    });
    const [loading, setLoading] = useState(true);

    // Get subscription from Redux store
    const subscription = useSelector((state) => state.auth.subscription);
    const user = useSelector((state) => state.auth.user);
    const tokens = useSelector((state) => state.auth.tokens);
    const orgId = user?.organization;

  
    const fetchData = async () => {
        try {
            // Fetch stats
            const statsData = await getScormStats();
            setStats(statsData);

            // Fetch and update subscription
            const subscriptionResponse = await getActiveSubscription();
            if (subscriptionResponse.success && subscriptionResponse.subscription) {
                // Update subscription in Redux store
                dispatch(
                    loginSuccess({
                        user,
                        tokens,
                        subscription: subscriptionResponse.subscription,
                    })
                );
            }
        } catch (error) {
            // console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };
    const onStatusUpdate = ({status}) => {
        if (status === "completed")
            fetchData()
    }
    useEffect(() => {
        fetchData();
    }, [dispatch]);

    if (loading) {
        return (
            <div className="stats-box d-flex gap-3 flex-wrap justify-content-start">
                <div className="stat-card bg-light text-dark position-relative">
                    <div className="stat-content">
                        <h5 className="fw-semibold">Loading...</h5>
                    </div>
                </div>
            </div>
        );
    }

    const quota = subscription?.quota;

    // Helper function to calculate remaining days
    const getRemainingDays = (endDate) => {
        if (!endDate) return 0;
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(diffDays, 0);
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    return (
        <>
        <FileStatusListener orgId={orgId} onJobStatusUpdate={onStatusUpdate} />
        {!loading && !subscription &&  <SubscriptionAlert
                    severity="error"
                    title="No Active Subscription"
                    prefixMessage="You do not have an active subscription. Please"
                    actionText="subscribe"
                    suffixMessage="to access all features."
                />}
            {/* Section 1: Usage Metrics */}
            <div style={{ marginBottom: '32px' }}>
                <h4
                    style={{
                        marginBottom: '16px',
                        color: '#333',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                    }}
                >
                    Usage Metrics
                </h4>
                <div className="custom_stats_container">
                    <div className="custom_stats orange">
                        <div className="stats_content">
                            <span>Total Courses Processed</span>
                            <span>{stats.total_files}</span>
                        </div>
                        <div className="icon">
                            <img src={files} alt="Files Icon" />
                        </div>
                    </div>
                    <div className="custom_stats blue">
                        <div className="stats_content">
                            <span>Storage Used</span>
                            <span>{(stats?.total_size_mb / 1024).toFixed(2)} GB</span>
                        </div>
                        <div className="icon">
                            <img src={space} alt="Space Icon" />
                        </div>
                    </div>
                    <div className="custom_stats green">
                        <div className="stats_content">
                            <span>Successful Extractions</span>
                            <span>
                                {stats.file_status_breakdown.find((e) => e.status === 'completed')?.count || 0}
                            </span>
                        </div>
                        <div className="icon">
                            <img src={completed} alt="Completed Icon" />
                        </div>
                    </div>
                    <div className="custom_stats red">
                        <div className="stats_content">
                            <span>Failed Extractions</span>
                            <span>
                                {stats?.file_status_breakdown?.reduce(
                                    (sum, { status, count }) =>
                                        status === 'rejected' || status === 'failed' ? sum + count : sum,
                                    0
                                ) || 0}
                            </span>
                        </div>
                        <div className="icon">
                            <img src={failed} alt="Failed Icon" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 2: Subscription Metrics */}
            {subscription && quota && (
                <div>
                    <h4
                        style={{
                            marginBottom: '16px',
                            color: '#333',
                            fontWeight: '600',
                            fontSize: '1.1rem',
                        }}
                    >
                        Subscription Metrics
                    </h4>
                    <div className="custom_stats_container" style={{}}>
                        <div className="custom_stats purple">
                            <div className="stats_content">
                                <span>Course Uploads</span>
                                <span>
                                    {quota.courses_uploaded} / {quota.course_included}
                                </span>
                                <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                    {Math.max(0, quota.course_included - quota.courses_uploaded)} remaining
                                </small>
                            </div>
                            <div className="icon">
                                <School sx={{ fontSize: '2.5rem', color: 'currentColor' }} />
                            </div>
                        </div>
                        <div className="custom_stats teal">
                            <div className="stats_content">
                                <span>Total Storage Allocation</span>
                                <span>
                                    {quota.storage_consumed_in_mb} / {quota.upload_quota} MB
                                </span>
                                <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                    {Math.max(0, quota.upload_quota - quota.storage_consumed_in_mb)} MB remaining
                                </small>
                            </div>
                            <div className="icon">
                                <Storage sx={{ fontSize: '2.5rem', color: 'currentColor' }} />
                            </div>
                        </div>
                        <div className="custom_stats pink">
                            <div className="stats_content">
                                <span>
                                    Media Duration Used
                                    <Tooltip 
                                        title="01 &quot;media hour&quot; - 01 hour of 1080p video or 5 hours of 128 kbps audio" 
                                        arrow 
                                        placement="top"
                                    >
                                        <InfoOutlined 
                                            sx={{ 
                                                fontSize: '0.875rem', 
                                                marginLeft: '0.5rem', 
                                                verticalAlign: 'middle',
                                                cursor: 'help',
                                                opacity: 0.7
                                            }} 
                                        />
                                    </Tooltip>
                                </span>
                                <span>
                                    {quota.media_hours_consumed_in_minutes} / {quota.media_hours_quota_in_minutes} min
                                </span>
                                <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                    {Math.max(0, quota.media_hours_quota_in_minutes - quota.media_hours_consumed_in_minutes)}{' '}
                                    min remaining
                                </small>
                            </div>
                            <div className="icon">
                                <VideoLibrary sx={{ fontSize: '2.5rem', color: 'currentColor' }} />
                            </div>
                        </div>
                        <div className="custom_stats indigo">
                            <div className="stats_content">
                                <span>Subscription Valid Till</span>
                                <span>{formatDate(subscription.end_date)}</span>
                                <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                    {getRemainingDays(subscription.end_date)} days remaining
                                </small>
                            </div>
                            <div className="icon">
                                <CalendarToday sx={{ fontSize: '2.5rem', color: 'currentColor' }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Stats;
